import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Drawer } from 'vaul';
import { RemoveScroll } from 'react-remove-scroll';
import cn from 'clsx';
import { last } from 'lodash-es';
import { useApplicationStore } from 'modules/application/store';
import { useSelector } from 'app/store';
import { devicesCustomClientFormOpenSelector } from 'modules/devices/slice';
import { settingsIsVisibleSelector } from 'modules/settings/slice';
import { isLandscape } from './util';
import { SNAP_POINTS_PORTRAIT, SNAP_POINTS_LANDSCAPE, HANDLE_PADDING } from './constants';

import classes from './BottomDrawer.module.scss';

interface Props {
  children: ReactNode;
  snapPoints?: (string | number)[];
}

type Snap = number | string | null;

const BottomDrawer = ({ children, snapPoints }: Props) => {
  const canvasAspect = useApplicationStore((state) => state.canvasAspect);
  const [contentHeight, setContentHeight] = useState(0);
  const measuredRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setContentHeight(node.getBoundingClientRect().height + HANDLE_PADDING);
    }
  }, []);
  const [points, setPoints] = useState(snapPoints ?? (isLandscape() ? SNAP_POINTS_LANDSCAPE : SNAP_POINTS_PORTRAIT));
  const getSnapPoints = useCallback(() => {
    if (isLandscape()) {
      return SNAP_POINTS_LANDSCAPE;
    }
    if (parseFloat(last(points) as string) !== contentHeight && window.innerHeight > contentHeight) {
      const newPoints = [...SNAP_POINTS_PORTRAIT.slice(0, -1), `${contentHeight}px`];
      setPoints(newPoints);
      return newPoints;
    }
    return points;
  }, [contentHeight, points]);
  const snapPointByAngle = useCallback(() => {
    if (isLandscape()) {
      return points[0];
    }
    return points[1];
  }, [points]);
  const [snap, setSnap] = useState<Snap>(snapPointByAngle);
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setSnap(lastSnap.current);
      } else {
        lastSnap.current = snap;
      }
    },
    [snap]
  );
  const lastSnap = useRef<Snap>(snap);
  const isSettingsVisible = useSelector(settingsIsVisibleSelector);
  const isClientCreateVisible = useSelector(devicesCustomClientFormOpenSelector);
  const isLast = useMemo(() => last(points) == snap, [snap, points]);
  useEffect(() => {
    setPoints(getSnapPoints());
    setSnap(snapPointByAngle);
  }, [canvasAspect, snapPointByAngle, setSnap, getSnapPoints]);

  return (
    <Drawer.Root
      snapPoints={points}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      dismissible={false}
      modal={false}
      open={!isSettingsVisible && !isClientCreateVisible}
      onOpenChange={onOpenChange}
    >
      <Drawer.Portal>
        <Drawer.Content className={cn(classes.wrapper, { [classes.rounded]: snap !== 1 })}>
          <div className={classes.handle} />
          <RemoveScroll
            enabled={isLast}
            className={cn(classes.content, { [classes.scrollable]: isLast })}
            ref={measuredRef}
          >
            {children}
            {/* next two lines to hide console warnings, maybe won't be needed in future updates */}
            <Drawer.Title className={classes.hide} aria-describedby="drawer-title" />
            <Drawer.Description className={classes.hide} aria-describedby="drawer-description" />
          </RemoveScroll>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default BottomDrawer;
